import React, { useState, useEffect } from 'react';
import {
    Card, Row, Col, Button, Badge, Modal, Form,
    Spinner, ProgressBar, Alert, Table, Accordion
} from 'react-bootstrap';
import {
    UserMinus, CheckCircle, Circle, Clock, ChevronRight,
    Users, AlertCircle, X, FileText, Star, BarChart2,
    Calendar, ArrowRight, Info, Calculator, Zap
} from 'lucide-react';
import { toast } from 'react-toastify';
import axiosInstance from '../../api/axiosConfig';
import { useAuth } from '../../context/AuthContext';
// ── Constants ────────────────────────────────────────────────────────────────
const HR_ROLES = ['super-admin','admin','hr-manager','account-manager'];
const STATUS_CONFIG = {
    pending_approval: { bg: 'warning',   label: 'Pending Approval',  step: 1 },
    approved:         { bg: 'info',      label: 'Approved',          step: 2 },
    clearance:        { bg: 'primary',   label: 'Clearance',         step: 3 },
    exit_interview:   { bg: 'warning',   label: 'Exit Interview',    step: 4 },
    settlement:       { bg: 'info',      label: 'Settlement',        step: 5 },
    completed:        { bg: 'success',   label: 'Completed',         step: 6 },
    rejected:         { bg: 'danger',    label: 'Rejected',          step: 0 },
};
const STEPS = [
    { key: 'pending_approval', label: 'Resignation', icon: '📝' },
    { key: 'approved',         label: 'Approved',    icon: '✅' },
    { key: 'clearance',        label: 'Clearance',   icon: '🔄' },
    { key: 'exit_interview',   label: 'Exit Talk',   icon: '💬' },
    { key: 'settlement',       label: 'Settlement',  icon: '💰' },
    { key: 'completed',        label: 'Done',        icon: '🎓' },
];
const REASON_CATEGORIES = [
    'Better Opportunity','Higher Salary','Career Growth',
    'Work-Life Balance','Relocation','Higher Education','Personal','Management','Other'
];
const EXIT_INTERVIEW_QUESTIONS = [
    { field: 'overall_experience', label: 'Overall experience at the company', type: 'rating5' },
    { field: 'manager_rating',     label: 'Your direct manager',               type: 'rating5' },
    { field: 'culture_rating',     label: 'Company culture and environment',   type: 'rating5' },
    { field: 'work_life_balance',  label: 'Work-life balance',                 type: 'rating5' },
    { field: 'recommend_company',  label: 'Would you recommend us as an employer? (0–10)', type: 'rating10' },
];
const formatDate = (d) => {
    if (!d) return '—';
    const [y,m,dd] = String(d).slice(0,10).split('-').map(Number);
    return new Date(y,m-1,dd).toLocaleDateString('en-IN',{ day:'numeric', month:'short', year:'numeric' });
};
const formatCurrency = (n) =>
    n ? `₹${parseFloat(n).toLocaleString('en-IN',{maximumFractionDigits:2})}` : '₹0';
// ── Progress stepper ─────────────────────────────────────────────────────────
const ProgressStepper = ({ status }) => {
    const current = STATUS_CONFIG[status]?.step || 0;
    return (
        <div className="d-flex align-items-center gap-0 mb-4 flex-wrap">
            {STEPS.map((s, i) => {
                const step  = STATUS_CONFIG[s.key]?.step || 0;
                const done  = step < current;
                const active = step === current;
                return (
                    <React.Fragment key={s.key}>
                        <div className="d-flex flex-column align-items-center" style={{ minWidth: 64 }}>
                            <div style={{
                                width: 36, height: 36, borderRadius: '50%',
                                background: done ? '#059669' : active ? '#4f46e5' : '#e5e7eb',
                                color: done || active ? '#fff' : '#9ca3af',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                fontSize: done ? 16 : 14, fontWeight: 600,
                                transition: 'all .3s',
                            }}>
                                {done ? '✓' : s.icon}
                            </div>
                            <div style={{ fontSize: 10, marginTop: 4, color: active ? '#4f46e5' : '#6b7280',
                                fontWeight: active ? 600 : 400, textAlign: 'center' }}>
                                {s.label}
                            </div>
                        </div>
                        {i < STEPS.length - 1 && (
                            <div style={{
                                flex: 1, height: 2, minWidth: 16,
                                background: done ? '#059669' : '#e5e7eb',
                                marginBottom: 20, transition: 'all .3s',
                            }}/>
                        )}
                    </React.Fragment>
                );
            })}
        </div>
    );
};
// ── Rating Stars ─────────────────────────────────────────────────────────────
const RatingStars = ({ value, max = 5, onChange, readOnly }) => (
    <div className="d-flex gap-1">
        {Array.from({ length: max }, (_, i) => i + 1).map(n => (
            <Star key={n} size={20}
                style={{ cursor: readOnly ? 'default' : 'pointer',
                    color: n <= (value || 0) ? '#f59e0b' : '#d1d5db',
                    fill: n <= (value || 0) ? '#f59e0b' : 'transparent' }}
                onClick={() => !readOnly && onChange && onChange(n)}
            />
        ))}
        {value && <span className="text-muted small ms-1">{value}/{max}</span>}
    </div>
);
// ════════════════════════════════════════════════════════════════════════════
const OffboardingPage = () => {
    const { user } = useAuth();
    const isHR = HR_ROLES.includes(user?.role);
    return isHR ? <HRView /> : <EmployeeView />;
};
// ════════════════════════════════════════════════════════════════════════════
// HR VIEW
// ════════════════════════════════════════════════════════════════════════════
const HRView = () => {
    const [tab,       setTab]       = useState('list');
    const [list,      setList]      = useState([]);
    const [stats,     setStats]     = useState(null);
    const [loading,   setLoading]   = useState(true);
    const [selected,  setSelected]  = useState(null);
    const [filter,    setFilter]    = useState('');
    const load = () => {
        setLoading(true);
        Promise.all([
            axiosInstance.get('/offboarding/all'),
            axiosInstance.get('/offboarding/stats'),
        ]).then(([l, s]) => {
            setList(l.data.data?.list || []);
            setStats(s.data.data);
        }).catch(() => toast.error('Failed to load'))
          .finally(() => setLoading(false));
    };
    useEffect(() => { load(); }, []);
    if (selected) return (
        <HRDetail item={selected} onBack={() => setSelected(null)} onRefresh={load} />
    );
    const filtered = list.filter(i => !filter || i.status === filter);
    return (
        <div className="p-2 p-md-4">
            <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-3">
                <div>
                    <h4 className="fw-bold mb-1">
                        <UserMinus size={22} className="me-2 text-danger"/>Offboarding
                    </h4>
                    <p className="text-muted small mb-0">
                        Manage employee exits — {list.filter(i => i.status !== 'completed').length} active
                    </p>
                </div>
                <div className="d-flex gap-2">
                    <Button size="sm" variant={tab==='list' ? 'primary' : 'outline-secondary'}
                        onClick={() => setTab('list')}><Users size={14} className="me-1"/>All Exits</Button>
                    <Button size="sm" variant={tab==='stats' ? 'primary' : 'outline-secondary'}
                        onClick={() => setTab('stats')}><BarChart2 size={14} className="me-1"/>Analytics</Button>
                </div>
            </div>
            {stats && (
                <Row className="g-3 mb-4">
                    {[
                        { label: 'Pending Review',   value: stats.pending     || 0, color: '#d97706' },
                        { label: 'In Progress',      value: stats.in_progress || 0, color: '#4f46e5' },
                        { label: 'Completed',        value: stats.completed   || 0, color: '#059669' },
                        { label: 'Avg Notice (days)', value: Math.round(stats.avg_notice_days || 0), color: '#0891b2' },
                    ].map((s, i) => (
                        <Col key={i} xs={6} lg={3}>
                            <Card className="border-0 shadow-sm p-3 text-center">
                                <div style={{ fontSize: 24, fontWeight: 700, color: s.color }}>{s.value}</div>
                                <div className="text-muted small">{s.label}</div>
                            </Card>
                        </Col>
                    ))}
                </Row>
            )}
            {tab === 'stats' && stats && (
                <Card className="border-0 shadow-sm p-4 mb-4">
                    <h6 className="fw-bold mb-3">Top reasons for leaving (last 6 months)</h6>
                    {(stats.top_reasons || []).length === 0 ? (
                        <p className="text-muted small">No data yet</p>
                    ) : stats.top_reasons.map((r, i) => (
                        <div key={i} className="d-flex align-items-center gap-3 mb-2">
                            <div style={{ minWidth: 160, fontSize: 13 }}>{r.reason_category}</div>
                            <ProgressBar now={(r.count / (stats.completed || 1)) * 100}
                                style={{ flex: 1, height: 8 }} variant="danger"/>
                            <div style={{ minWidth: 24, fontSize: 13, textAlign: 'right' }}>{r.count}</div>
                        </div>
                    ))}
                </Card>
            )}
            <div className="d-flex gap-2 mb-3 flex-wrap">
                {['','pending_approval','approved','clearance','exit_interview','settlement','completed'].map(s => (
                    <Button key={s} size="sm"
                        variant={filter === s ? 'primary' : 'outline-secondary'}
                        onClick={() => setFilter(s)}>
                        {s ? STATUS_CONFIG[s]?.label : 'All'}
                        {s && <Badge bg="light" text="dark" className="ms-1">
                            {list.filter(i => i.status === s).length}
                        </Badge>}
                    </Button>
                ))}
            </div>
            {loading ? (
                <div className="text-center py-5"><Spinner animation="border" variant="danger"/></div>
            ) : (
                <Card className="border-0 shadow-sm">
                    {filtered.length === 0 ? (
                        <div className="text-center py-5 text-muted">
                            <UserMinus size={36} className="mb-2 opacity-50"/>
                            <p>No offboarding records{filter ? ' matching filter' : ' yet'}</p>
                        </div>
                    ) : (
                        <Table hover responsive className="mb-0 small align-middle">
                            <thead className="bg-light">
                                <tr className="text-muted text-uppercase" style={{ fontSize: 11 }}>
                                    <th className="ps-4">Employee</th>
                                    <th>Type</th>
                                    <th>Resignation</th>
                                    <th>Last Day</th>
                                    <th>Clearance</th>
                                    <th>Status</th>
                                    <th className="pe-4 text-end">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filtered.map(item => (
                                    <tr key={item.id} style={{ cursor: 'pointer' }}
                                        onClick={() => setSelected(item)}>
                                        <td className="ps-4">
                                            <div className="fw-medium">{item.employee_name}</div>
                                            <div className="text-muted" style={{ fontSize: 11 }}>
                                                {item.employee_code} · {item.department || '—'}
                                            </div>
                                        </td>
                                        <td><Badge bg="light" text="dark">{item.exit_type}</Badge></td>
                                        <td>{formatDate(item.resignation_date)}</td>
                                        <td>{formatDate(item.last_working_day)}</td>
                                        <td>
                                            {item.total_clearance ? (
                                                <div style={{ minWidth: 100 }}>
                                                    <ProgressBar
                                                        now={Math.round((item.cleared/item.total_clearance)*100)}
                                                        style={{ height: 5 }} variant="info"/>
                                                    <div style={{ fontSize: 10 }} className="text-muted mt-1">
                                                        {item.cleared}/{item.total_clearance}
                                                    </div>
                                                </div>
                                            ) : <span className="text-muted">—</span>}
                                        </td>
                                        <td>
                                            <Badge bg={STATUS_CONFIG[item.status]?.bg || 'secondary'}>
                                                {STATUS_CONFIG[item.status]?.label || item.status}
                                            </Badge>
                                        </td>
                                        <td className="pe-4 text-end">
                                            <Button size="sm" variant="outline-danger" className="py-0">
                                                View <ChevronRight size={13}/>
                                            </Button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    )}
                </Card>
            )}
        </div>
    );
};
// ── HR Detail View ───────────────────────────────────────────────────────────
const HRDetail = ({ item, onBack, onRefresh }) => {
    const [data,       setData]       = useState(null);
    const [loading,    setLoading]    = useState(true);
    const [lwdForm,    setLwdForm]    = useState({ last_working_day: '' });
    const [settlementForm, setSettlementForm] = useState({
        total_unpaid_salary: '', leave_encashment: '', gratuity: '',
        other_deductions: '', settlement_date: '', settlement_notes: ''
    });
    const [saving,     setSaving]     = useState('');
    // NEW: auto-calculate state
    const [preview,    setPreview]    = useState(null);
    const [previewing, setPreviewing] = useState(false);

    const load = () => {
        setLoading(true);
        axiosInstance.get(`/offboarding/${item.id}`)
            .then(r => {
                const d = r.data.data;
                setData(d);
                setLwdForm({ last_working_day: d.last_working_day?.slice(0,10) || '' });
                setSettlementForm({
                    total_unpaid_salary: d.total_unpaid_salary || '',
                    leave_encashment:    d.leave_encashment || '',
                    gratuity:            d.gratuity || '',
                    other_deductions:    d.other_deductions || '',
                    settlement_date:     d.settlement_date?.slice(0,10) || '',
                    settlement_notes:    d.settlement_notes || '',
                });
            })
            .catch(() => toast.error('Failed to load detail'))
            .finally(() => setLoading(false));
    };
    useEffect(() => { load(); }, [item.id]);

    const action = async (endpoint, payload, label) => {
        setSaving(label);
        try {
            await axiosInstance.patch(`/offboarding/${item.id}/${endpoint}`, payload);
            toast.success(`${label} successful`);
            load(); onRefresh();
        } catch (err) { toast.error(err.response?.data?.message || `${label} failed`); }
        finally { setSaving(''); }
    };

    const completeTask = async (taskId) => {
        try {
            await axiosInstance.patch(`/offboarding/clearance/${taskId}/complete`);
            toast.success('Task completed');
            load();
        } catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
    };

    // NEW: auto-calculate F&F settlement from payroll & leave data
    const autoCalculate = async () => {
        setPreviewing(true);
        setPreview(null);
        try {
            const res = await axiosInstance.get(`/offboarding/${item.id}/settlement-preview`);
            const p   = res.data.data;
            setPreview(p);
            // Pre-fill form with calculated values — HR can still override any field
            setSettlementForm(prev => ({
                ...prev,
                total_unpaid_salary: String(p.unpaid_salary   || 0),
                leave_encashment:    String(p.leave_encashment || 0),
                gratuity:            String(p.gratuity         || 0),
                other_deductions:    String(p.notice_deduction || 0),
            }));
            toast.success('Settlement auto-calculated from payroll and leave records');
        } catch (err) {
            toast.error(err.response?.data?.message || 'Auto-calculation failed');
        } finally { setPreviewing(false); }
    };

    if (loading) return <div className="text-center py-5"><Spinner animation="border" variant="danger"/></div>;
    if (!data)   return null;

    const netSettlement = (
        (parseFloat(settlementForm.total_unpaid_salary) || 0) +
        (parseFloat(settlementForm.leave_encashment) || 0) +
        (parseFloat(settlementForm.gratuity) || 0) -
        (parseFloat(settlementForm.other_deductions) || 0)
    );

    return (
        <div className="p-2 p-md-4">
            <Button variant="outline-secondary" size="sm" className="mb-3" onClick={onBack}>← Back</Button>
            {/* Header card */}
            <Card className="border-0 shadow-sm mb-4 p-4"
                style={{ background: 'linear-gradient(135deg,#fef2f2,#fce7e7)' }}>
                <div className="d-flex justify-content-between align-items-start flex-wrap gap-3">
                    <div>
                        <h5 className="fw-bold mb-1">{data.employee_name}</h5>
                        <div className="text-muted small mb-2">
                            {data.department} · {data.designation} · {data.employee_email}
                        </div>
                        <div className="d-flex gap-2 flex-wrap">
                            <Badge bg="light" text="dark">{data.exit_type}</Badge>
                            <Badge bg={STATUS_CONFIG[data.status]?.bg || 'secondary'}>
                                {STATUS_CONFIG[data.status]?.label}
                            </Badge>
                            {data.years_of_service && (
                                <Badge bg="light" text="dark">
                                    {parseFloat(data.years_of_service).toFixed(1)} yrs service
                                </Badge>
                            )}
                        </div>
                    </div>
                    <div className="text-end">
                        <div className="text-muted small">Resigned</div>
                        <div className="fw-bold">{formatDate(data.resignation_date)}</div>
                        <div className="text-muted small mt-1">Last day</div>
                        <div className="fw-bold">{formatDate(data.last_working_day)}</div>
                    </div>
                </div>
                <ProgressStepper status={data.status} />
            </Card>
            {/* Action panels */}
            <Row className="g-4">
                <Col lg={8}>
                    {/* Review panel */}
                    {data.status === 'pending_approval' && (
                        <Card className="border-0 shadow-sm mb-4 p-4">
                            <h6 className="fw-bold mb-3">Review Resignation</h6>
                            <p className="text-muted small mb-3">
                                <strong>Reason:</strong> {data.reason || 'Not provided'}<br/>
                                <strong>Category:</strong> {data.reason_category}<br/>
                                <strong>Manager:</strong> {data.manager_name || '—'}
                            </p>
                            <div className="d-flex gap-2 flex-wrap">
                                <Button variant="success" disabled={!!saving}
                                    onClick={() => action('review', { action:'approve' }, 'Approve')}>
                                    {saving==='Approve' ? <Spinner size="sm"/> : '✓ Approve Resignation'}
                                </Button>
                                <Button variant="outline-danger" disabled={!!saving}
                                    onClick={() => {
                                        const rem = prompt('Reason for rejection?');
                                        if (rem !== null) action('review', { action:'reject', remarks: rem }, 'Reject');
                                    }}>
                                    ✗ Reject
                                </Button>
                                <Button variant="outline-primary" disabled={!!saving}
                                    onClick={() => {
                                        const lwd = prompt('Counter with new last working day (YYYY-MM-DD):');
                                        if (lwd) action('review', { action:'counter', counter_lwd: lwd }, 'Counter');
                                    }}>
                                    ↔ Counter Offer
                                </Button>
                            </div>
                        </Card>
                    )}
                    {/* Set LWD panel */}
                    {data.status === 'approved' && (
                        <Card className="border-0 shadow-sm mb-4 p-4">
                            <h6 className="fw-bold mb-3">Confirm Last Working Day & Start Clearance</h6>
                            <Alert variant="info" className="small py-2">
                                <Info size={14} className="me-1"/>
                                Confirming LWD will automatically create the clearance checklist.
                            </Alert>
                            <div className="d-flex gap-3 align-items-end mt-3">
                                <div className="flex-grow-1">
                                    <Form.Label className="small fw-bold">Last Working Day *</Form.Label>
                                    <Form.Control type="date" value={lwdForm.last_working_day}
                                        onChange={e => setLwdForm({ last_working_day: e.target.value })}/>
                                </div>
                                <Button variant="primary" disabled={!lwdForm.last_working_day || !!saving}
                                    onClick={() => action('set-lwd', lwdForm, 'Set LWD')}>
                                    {saving === 'Set LWD' ? <Spinner size="sm"/> : 'Confirm & Start Clearance'}
                                </Button>
                            </div>
                        </Card>
                    )}
                    {/* Clearance tasks */}
                    {['clearance','exit_interview','settlement','completed'].includes(data.status) && (
                        <Card className="border-0 shadow-sm mb-4">
                            <Card.Header className="bg-white border-0 pt-3 d-flex justify-content-between align-items-center">
                                <span className="fw-bold">Clearance Checklist</span>
                                <Badge bg={data.all_cleared ? 'success' : 'primary'}>
                                    {data.clearance_pct}% complete
                                </Badge>
                            </Card.Header>
                            <div style={{ padding: '0 0 8px' }}>
                                {(data.clearance || []).map(task => (
                                    <div key={task.id}
                                        className="d-flex align-items-center gap-3 px-4 py-2"
                                        style={{ borderBottom: '0.5px solid #f1f5f9' }}>
                                        {task.status === 'completed'
                                            ? <CheckCircle size={18} className="text-success flex-shrink-0"/>
                                            : <Circle size={18} className="text-muted flex-shrink-0"/>}
                                        <div className="flex-grow-1">
                                            <div className="small fw-medium">{task.title}
                                                {task.is_required && (
                                                    <span className="ms-1 text-danger" style={{ fontSize: 10 }}>*required</span>
                                                )}
                                            </div>
                                            <div className="text-muted" style={{ fontSize: 11 }}>
                                                {task.assigned_dept}
                                                {task.due_date && ` · Due ${formatDate(task.due_date)}`}
                                                {task.status === 'completed' && task.completed_by_name && (
                                                    <span className="text-success ms-1">· Done by {task.completed_by_name}</span>
                                                )}
                                            </div>
                                        </div>
                                        {task.status !== 'completed' && data.status !== 'completed' && (
                                            <Button size="sm" variant="outline-success" className="py-0 flex-shrink-0"
                                                onClick={() => completeTask(task.id)}>
                                                <CheckCircle size={13}/> Done
                                            </Button>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </Card>
                    )}
                    {/* Exit interview */}
                    {['exit_interview','settlement','completed'].includes(data.status) && (
                        <Card className="border-0 shadow-sm mb-4 p-4">
                            <h6 className="fw-bold mb-3">Exit Interview</h6>
                            {data.interview ? (
                                <div>
                                    <Row className="g-3">
                                        {EXIT_INTERVIEW_QUESTIONS.map(q => (
                                            <Col key={q.field} xs={12} md={6}>
                                                <div className="small fw-bold mb-1">{q.label}</div>
                                                <RatingStars value={data.interview[q.field]}
                                                    max={q.type === 'rating10' ? 10 : 5} readOnly/>
                                            </Col>
                                        ))}
                                        <Col xs={12}>
                                            <div className="small fw-bold mb-1">Primary reason for leaving</div>
                                            <div className="text-muted small">{data.interview.reason_primary || '—'}</div>
                                        </Col>
                                        {data.interview.feedback && (
                                            <Col xs={12}>
                                                <div className="small fw-bold mb-1">Feedback</div>
                                                <div className="small">{data.interview.feedback}</div>
                                            </Col>
                                        )}
                                        {data.interview.suggestions && (
                                            <Col xs={12}>
                                                <div className="small fw-bold mb-1">Suggestions</div>
                                                <div className="small">{data.interview.suggestions}</div>
                                            </Col>
                                        )}
                                        <Col xs={12}>
                                            <Badge bg={data.interview.would_return ? 'success' : 'secondary'}>
                                                {data.interview.would_return ? 'Would return' : 'Would not return'}
                                            </Badge>
                                        </Col>
                                    </Row>
                                </div>
                            ) : (
                                <Alert variant="warning" className="small py-2">
                                    Exit interview not yet submitted by the employee.
                                </Alert>
                            )}
                        </Card>
                    )}
                </Col>
                <Col lg={4}>
                    {/* ── Full & Final Settlement — ENHANCED ─────────────────── */}
                    {['settlement','completed'].includes(data.status) && (
                        <Card className="border-0 shadow-sm mb-4 p-4">
                            <div className="d-flex align-items-center justify-content-between mb-3">
                                <h6 className="fw-bold mb-0">
                                    <Calculator size={16} className="me-2 text-primary"/>
                                    Full & Final Settlement
                                </h6>
                                {data.status !== 'completed' && (
                                    <Button
                                        size="sm"
                                        variant="outline-success"
                                        onClick={autoCalculate}
                                        disabled={previewing}
                                        className="d-flex align-items-center gap-1"
                                        title="Auto-calculate from payroll & leave data">
                                        {previewing
                                            ? <Spinner size="sm"/>
                                            : <Zap size={13}/>}
                                        {previewing ? 'Calculating…' : 'Auto-Calculate'}
                                    </Button>
                                )}
                            </div>

                            {/* Service summary from preview */}
                            {preview && (
                                <div className="rounded-3 p-3 mb-3"
                                    style={{ background: '#f8fafc', border: '1px solid #e2e8f0' }}>
                                    <div className="small fw-bold text-muted mb-2">
                                        EMPLOYEE SERVICE SUMMARY
                                    </div>
                                    <div className="d-flex justify-content-between small mb-1">
                                        <span className="text-muted">Years of Service</span>
                                        <span className="fw-bold">{preview.years_of_service} yrs</span>
                                    </div>
                                    <div className="d-flex justify-content-between small mb-1">
                                        <span className="text-muted">Last Gross Salary</span>
                                        <span className="fw-bold">{formatCurrency(preview.supporting?.last_gross)}</span>
                                    </div>
                                    <div className="d-flex justify-content-between small">
                                        <span className="text-muted">Gratuity Eligible</span>
                                        <Badge bg={preview.supporting?.gratuity_eligible ? 'success' : 'secondary'}
                                            style={{ fontSize: 10 }}>
                                            {preview.supporting?.gratuity_eligible ? '✓ Yes (≥5 yrs)' : '✗ No (<5 yrs)'}
                                        </Badge>
                                    </div>
                                </div>
                            )}

                            <Row className="g-2">
                                {/* Unpaid Salary */}
                                <Col xs={12}>
                                    <Form.Label className="small fw-bold mb-1">Unpaid Salary</Form.Label>
                                    <Form.Control type="number" placeholder="0" size="sm"
                                        value={settlementForm.total_unpaid_salary}
                                        onChange={e => setSettlementForm(p => ({...p, total_unpaid_salary: e.target.value}))}
                                        readOnly={data.status === 'completed'}/>
                                    {preview?.formulas?.unpaid_salary && (
                                        <div className="text-muted mt-1" style={{ fontSize: 11 }}>
                                            📋 {preview.formulas.unpaid_salary}
                                        </div>
                                    )}
                                </Col>

                                {/* Leave Encashment */}
                                <Col xs={12}>
                                    <Form.Label className="small fw-bold mb-1">Leave Encashment</Form.Label>
                                    <Form.Control type="number" placeholder="0" size="sm"
                                        value={settlementForm.leave_encashment}
                                        onChange={e => setSettlementForm(p => ({...p, leave_encashment: e.target.value}))}
                                        readOnly={data.status === 'completed'}/>
                                    {preview?.formulas?.leave_encashment && (
                                        <div className="text-muted mt-1" style={{ fontSize: 11 }}>
                                            📋 {preview.formulas.leave_encashment}
                                        </div>
                                    )}
                                </Col>

                                {/* Gratuity */}
                                <Col xs={12}>
                                    <Form.Label className="small fw-bold mb-1">
                                        Gratuity
                                        {preview && !preview.supporting?.gratuity_eligible && (
                                            <Badge bg="secondary" className="ms-2" style={{ fontSize: 9 }}>
                                                Not Eligible
                                            </Badge>
                                        )}
                                    </Form.Label>
                                    <Form.Control type="number" placeholder="0" size="sm"
                                        value={settlementForm.gratuity}
                                        onChange={e => setSettlementForm(p => ({...p, gratuity: e.target.value}))}
                                        readOnly={data.status === 'completed'}/>
                                    {preview?.formulas?.gratuity && (
                                        <div className="text-muted mt-1" style={{ fontSize: 11 }}>
                                            📋 {preview.formulas.gratuity}
                                        </div>
                                    )}
                                </Col>

                                {/* Deductions */}
                                <Col xs={12}>
                                    <Form.Label className="small fw-bold mb-1 text-danger">
                                        Deductions (−)
                                    </Form.Label>
                                    <Form.Control type="number" placeholder="0" size="sm"
                                        value={settlementForm.other_deductions}
                                        onChange={e => setSettlementForm(p => ({...p, other_deductions: e.target.value}))}
                                        readOnly={data.status === 'completed'}/>
                                    {preview?.formulas?.notice_deduction && (
                                        <div className="text-muted mt-1" style={{ fontSize: 11 }}>
                                            📋 {preview.formulas.notice_deduction}
                                        </div>
                                    )}
                                </Col>

                                {/* Net Settlement */}
                                <Col xs={12}>
                                    <div className="p-3 rounded mt-2"
                                        style={{ background: '#f0fdf4', border: '1px solid #86efac' }}>
                                        <div className="fw-bold text-success" style={{ fontSize: 18 }}>
                                            {formatCurrency(netSettlement)}
                                        </div>
                                        <div className="text-muted small">Net Settlement</div>
                                    </div>
                                </Col>

                                {/* Settlement Date */}
                                <Col xs={12}>
                                    <Form.Label className="small fw-bold mb-1">Settlement Date</Form.Label>
                                    <Form.Control type="date" size="sm"
                                        value={settlementForm.settlement_date}
                                        onChange={e => setSettlementForm(p => ({...p, settlement_date: e.target.value}))}
                                        readOnly={data.status === 'completed'}/>
                                </Col>

                                {/* Notes */}
                                <Col xs={12}>
                                    <Form.Label className="small fw-bold mb-1">Notes</Form.Label>
                                    <Form.Control as="textarea" rows={2} size="sm"
                                        placeholder="Any additional notes…"
                                        value={settlementForm.settlement_notes}
                                        onChange={e => setSettlementForm(p => ({...p, settlement_notes: e.target.value}))}
                                        readOnly={data.status === 'completed'}/>
                                </Col>

                                {data.status !== 'completed' && (
                                    <Col xs={12} className="mt-2">
                                        <Button variant="primary" className="w-100" size="sm"
                                            disabled={!!saving}
                                            onClick={() => action('settlement', settlementForm, 'Settlement')}>
                                            {saving === 'Settlement' ? <Spinner size="sm"/> : 'Save Settlement'}
                                        </Button>
                                        {preview && (
                                            <div className="text-muted text-center mt-2" style={{ fontSize: 11 }}>
                                                💡 Values auto-filled from payroll data. You can edit before saving.
                                            </div>
                                        )}
                                    </Col>
                                )}
                            </Row>
                        </Card>
                    )}
                    {/* Complete button */}
                    {data.status === 'settlement' && (
                        <Card className="border-0 shadow-sm p-4 mb-4">
                            <h6 className="fw-bold mb-2">Mark Complete</h6>
                            <p className="text-muted small mb-3">
                                This will deactivate the employee account. All required clearance must be done first.
                            </p>
                            <Button variant="danger" className="w-100" disabled={!!saving}
                                onClick={() => {
                                    if (window.confirm('Mark this offboarding as complete and deactivate the employee account?'))
                                        action('complete', {}, 'Complete');
                                }}>
                                {saving === 'Complete' ? <Spinner size="sm"/> : '🎓 Complete Offboarding'}
                            </Button>
                        </Card>
                    )}
                </Col>
            </Row>
        </div>
    );
};
// ════════════════════════════════════════════════════════════════════════════
// EMPLOYEE VIEW
// ════════════════════════════════════════════════════════════════════════════
const EmployeeView = () => {
    const [data,          setData]          = useState(null);
    const [loading,       setLoading]       = useState(true);
    const [showResign,    setShowResign]    = useState(false);
    const [showInterview, setShowInterview] = useState(false);
    const load = () => {
        setLoading(true);
        axiosInstance.get('/offboarding/my')
            .then(r => setData(r.data.data))
            .catch(() => {})
            .finally(() => setLoading(false));
    };
    useEffect(() => { load(); }, []);
    if (loading) return <div className="text-center py-5"><Spinner animation="border" variant="danger"/></div>;
    if (!data) return (
        <div className="p-2 p-md-4">
            <Card className="border-0 shadow-sm p-5 text-center">
                <UserMinus size={48} className="mx-auto mb-3 text-muted opacity-40"/>
                <h5 className="mb-2">No active offboarding</h5>
                <p className="text-muted small mb-4">
                    Use the button below to submit your resignation through the proper channel.
                </p>
                <Button variant="outline-danger" onClick={() => setShowResign(true)}>
                    Submit Resignation
                </Button>
            </Card>
            {showResign && (
                <ResignModal onClose={() => setShowResign(false)} onDone={load} />
            )}
        </div>
    );
    const canSubmitInterview = data.status === 'exit_interview' && !data.interview;
    return (
        <div className="p-2 p-md-4">
            <h4 className="fw-bold mb-4">
                <UserMinus size={22} className="me-2 text-danger"/>My Offboarding
            </h4>
            <Card className="border-0 shadow-sm mb-4 p-4"
                style={{ background: 'linear-gradient(135deg,#fef2f2,#fce7e7)' }}>
                <Row className="align-items-center">
                    <Col>
                        <div className="fw-bold mb-1">
                            Resignation submitted {formatDate(data.resignation_date)}
                        </div>
                        <div className="text-muted small">
                            Last working day: <strong>{formatDate(data.last_working_day)}</strong>
                        </div>
                        {data.review_remarks && (
                            <Alert variant="info" className="small py-1 px-2 mt-2 mb-0">
                                HR note: {data.review_remarks}
                            </Alert>
                        )}
                    </Col>
                    <Col xs="auto">
                        <Badge bg={STATUS_CONFIG[data.status]?.bg || 'secondary'}
                            style={{ fontSize: 13, padding: '8px 12px' }}>
                            {STATUS_CONFIG[data.status]?.label}
                        </Badge>
                    </Col>
                </Row>
                <div className="mt-4">
                    <ProgressStepper status={data.status} />
                </div>
            </Card>
            {(data.clearance || []).length > 0 && (
                <Card className="border-0 shadow-sm mb-4 p-4">
                    <h6 className="fw-bold mb-3">Clearance Status</h6>
                    {data.clearance.map(task => (
                        <div key={task.id} className="d-flex align-items-center gap-2 mb-2">
                            {task.status === 'completed'
                                ? <CheckCircle size={16} className="text-success flex-shrink-0"/>
                                : <Clock size={16} className="text-muted flex-shrink-0"/>}
                            <div className="small flex-grow-1">{task.title}</div>
                            <Badge bg="light" text="dark" style={{ fontSize: 10 }}>{task.assigned_dept}</Badge>
                            <Badge bg={task.status === 'completed' ? 'success' : 'secondary'} style={{ fontSize: 10 }}>
                                {task.status}
                            </Badge>
                        </div>
                    ))}
                </Card>
            )}
            {canSubmitInterview && (
                <Card className="border-0 shadow-sm mb-4 p-4"
                    style={{ borderLeft: '4px solid #f59e0b', borderRadius: '0 12px 12px 0' }}>
                    <h6 className="fw-bold mb-2">Your exit interview is ready</h6>
                    <p className="text-muted small mb-3">
                        Please take 5 minutes to share your experience. Your feedback is anonymous and helps us improve.
                    </p>
                    <Button variant="warning" onClick={() => setShowInterview(true)}>
                        Complete Exit Interview <ArrowRight size={14} className="ms-1"/>
                    </Button>
                </Card>
            )}
            {['settlement','completed'].includes(data.status) && data.final_settlement_amount > 0 && (
                <Card className="border-0 shadow-sm p-4"
                    style={{ borderLeft: '4px solid #059669', borderRadius: '0 12px 12px 0' }}>
                    <h6 className="fw-bold mb-3">Final Settlement</h6>
                    <Row className="g-2">
                        {[
                            { label: 'Unpaid Salary',    val: data.total_unpaid_salary },
                            { label: 'Leave Encashment', val: data.leave_encashment },
                            { label: 'Gratuity',         val: data.gratuity },
                            { label: 'Deductions',       val: data.other_deductions, neg: true },
                        ].map((r, i) => r.val > 0 && (
                            <Col key={i} xs={6}>
                                <div className="small text-muted">{r.label}</div>
                                <div className="fw-medium" style={{ color: r.neg ? '#dc2626' : undefined }}>
                                    {r.neg ? '-' : ''}{formatCurrency(r.val)}
                                </div>
                            </Col>
                        ))}
                        <Col xs={12}>
                            <div className="p-3 mt-2 rounded"
                                style={{ background: '#f0fdf4', border: '1px solid #86efac' }}>
                                <div className="fw-bold text-success" style={{ fontSize: 20 }}>
                                    {formatCurrency(data.final_settlement_amount)}
                                </div>
                                <div className="text-muted small">
                                    Net Settlement · Due {formatDate(data.settlement_date)}
                                </div>
                            </div>
                        </Col>
                    </Row>
                </Card>
            )}
            {showInterview && (
                <ExitInterviewModal
                    offboardingId={data.id}
                    onClose={() => setShowInterview(false)}
                    onDone={() => { setShowInterview(false); load(); }}
                />
            )}
        </div>
    );
};
// ── Resign Modal ─────────────────────────────────────────────────────────────
const ResignModal = ({ onClose, onDone }) => {
    const [form, setForm]   = useState({
        resignation_date: '', last_working_day: '', reason: '',
        reason_category: 'Better Opportunity', exit_type: 'Resignation'
    });
    const [saving, setSaving] = useState(false);
    const submit = async () => {
        if (!form.resignation_date) return toast.error('Resignation date is required');
        setSaving(true);
        try {
            await axiosInstance.post('/offboarding/resign', form);
            toast.success('Resignation submitted. HR will be notified.');
            onClose(); onDone();
        } catch (err) { toast.error(err.response?.data?.message || 'Failed to submit'); }
        finally { setSaving(false); }
    };
    return (
        <Modal show onHide={onClose} centered>
            <Modal.Header closeButton className="border-0">
                <Modal.Title className="fw-bold fs-6">
                    <UserMinus size={18} className="me-2 text-danger"/>Submit Resignation
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Alert variant="warning" className="small py-2 mb-3">
                    Once submitted, your manager and HR will be notified immediately.
                </Alert>
                <Row className="g-3">
                    <Col xs={6}>
                        <Form.Label className="small fw-bold">Resignation Date *</Form.Label>
                        <Form.Control type="date" value={form.resignation_date}
                            onChange={e => setForm(p => ({...p, resignation_date: e.target.value}))}/>
                    </Col>
                    <Col xs={6}>
                        <Form.Label className="small fw-bold">Proposed Last Day</Form.Label>
                        <Form.Control type="date" value={form.last_working_day}
                            onChange={e => setForm(p => ({...p, last_working_day: e.target.value}))}/>
                    </Col>
                    <Col xs={12}>
                        <Form.Label className="small fw-bold">Primary Reason</Form.Label>
                        <Form.Select value={form.reason_category}
                            onChange={e => setForm(p => ({...p, reason_category: e.target.value}))}>
                            {REASON_CATEGORIES.map(r => <option key={r} value={r}>{r}</option>)}
                        </Form.Select>
                    </Col>
                    <Col xs={12}>
                        <Form.Label className="small fw-bold">Additional Details (optional)</Form.Label>
                        <Form.Control as="textarea" rows={3} placeholder="Share any additional context…"
                            value={form.reason} onChange={e => setForm(p => ({...p, reason: e.target.value}))}/>
                    </Col>
                </Row>
                <div className="d-flex gap-2 mt-4">
                    <Button variant="light" className="flex-grow-1" onClick={onClose}>Cancel</Button>
                    <Button variant="danger" className="flex-grow-1 fw-bold" onClick={submit} disabled={saving}>
                        {saving ? <Spinner size="sm"/> : 'Submit Resignation'}
                    </Button>
                </div>
            </Modal.Body>
        </Modal>
    );
};
// ── Exit Interview Modal ─────────────────────────────────────────────────────
const ExitInterviewModal = ({ offboardingId, onClose, onDone }) => {
    const [form, setForm]   = useState({
        overall_experience: 0, manager_rating: 0, culture_rating: 0,
        work_life_balance: 0, recommend_company: 0,
        reason_primary: 'Better Opportunity', reason_detail: '',
        feedback: '', suggestions: '', would_return: false,
    });
    const [saving, setSaving] = useState(false);
    const submit = async () => {
        if (!form.overall_experience) return toast.error('Please rate your overall experience');
        setSaving(true);
        try {
            await axiosInstance.post(`/offboarding/${offboardingId}/exit-interview`, form);
            toast.success('Exit interview submitted. Thank you!');
            onDone();
        } catch (err) { toast.error(err.response?.data?.message || 'Failed to submit'); }
        finally { setSaving(false); }
    };
    const set = (field, val) => setForm(p => ({ ...p, [field]: val }));
    return (
        <Modal show onHide={onClose} centered size="lg">
            <Modal.Header closeButton className="border-0">
                <Modal.Title className="fw-bold fs-6">Exit Interview</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <p className="text-muted small mb-4">
                    Your feedback is confidential and helps us create a better workplace.
                    This takes about 3 minutes.
                </p>
                <Row className="g-4">
                    {EXIT_INTERVIEW_QUESTIONS.map(q => (
                        <Col key={q.field} xs={12} md={6}>
                            <Form.Label className="small fw-bold mb-2">{q.label}</Form.Label>
                            <RatingStars value={form[q.field]}
                                max={q.type === 'rating10' ? 10 : 5}
                                onChange={v => set(q.field, v)}/>
                        </Col>
                    ))}
                    <Col xs={12} md={6}>
                        <Form.Label className="small fw-bold">Primary reason for leaving</Form.Label>
                        <Form.Select value={form.reason_primary}
                            onChange={e => set('reason_primary', e.target.value)}>
                            {REASON_CATEGORIES.map(r => <option key={r} value={r}>{r}</option>)}
                        </Form.Select>
                    </Col>
                    <Col xs={12} md={6}>
                        <Form.Check type="switch" label="I would consider returning to the company"
                            checked={form.would_return}
                            onChange={e => set('would_return', e.target.checked)}/>
                    </Col>
                    <Col xs={12}>
                        <Form.Label className="small fw-bold">Additional feedback</Form.Label>
                        <Form.Control as="textarea" rows={3}
                            placeholder="What did you enjoy most? What could have been better?"
                            value={form.feedback} onChange={e => set('feedback', e.target.value)}/>
                    </Col>
                    <Col xs={12}>
                        <Form.Label className="small fw-bold">Suggestions for improvement</Form.Label>
                        <Form.Control as="textarea" rows={2}
                            placeholder="What would you recommend the company change or improve?"
                            value={form.suggestions} onChange={e => set('suggestions', e.target.value)}/>
                    </Col>
                </Row>
                <div className="d-flex gap-2 mt-4">
                    <Button variant="light" className="flex-grow-1" onClick={onClose}>Skip for now</Button>
                    <Button variant="warning" className="flex-grow-1 fw-bold" onClick={submit} disabled={saving}>
                        {saving ? <Spinner size="sm"/> : 'Submit Interview'}
                    </Button>
                </div>
            </Modal.Body>
        </Modal>
    );
};
export default OffboardingPage;
